import { PlaceTypeOrderMode } from '~/gql/graphql';

/**
 * Normalize, dedupe and sort strings case-insensitively
 * Filters out empty strings and "other" (case-insensitive)
 */
export const uniqueSorted = (values: (string | undefined)[]): string[] => {
  const normalized = values
    .map((v) => (typeof v === 'string' ? v.trim() : v))
    .filter((v): v is string => !!v)
    .filter((t) => t.toLowerCase() !== 'other');
  const deduped = Array.from(new Set(normalized));
  return deduped.sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true })
  );
};

/**
 * Extract and clean configured types from config query result
 */
export const extractConfiguredTypes = (
  configData: { assetTypeConfig?: { types?: unknown } } | undefined
): string[] => {
  const configured: string[] = Array.isArray(configData?.assetTypeConfig?.types)
    ? (configData?.assetTypeConfig?.types as string[])
    : [];
  return configured.filter((value: string) => value.trim() !== '');
};

/**
 * Extract and clean in-use types from asset filters query result
 */
export const extractInUseTypes = (
  resultData: { assetTypes?: unknown } | undefined
): string[] => {
  const inUseRaw: unknown = resultData?.assetTypes;
  const inUse: string[] = Array.isArray(inUseRaw) ? (inUseRaw as string[]) : [];
  return inUse.filter((value: string) => value.trim() !== '');
};

/**
 * Get in-use types that aren't in configured list (extras)
 */
export const getExtras = (
  configuredClean: string[],
  inUseClean: string[]
): string[] => {
  return uniqueSorted(
    inUseClean.filter((t: string) => !configuredClean.includes(t))
  );
};

/**
 * Merge configured, extras, and newly created types
 * If no configured types exist, use sorted in-use types instead
 */
export const mergeTypes = (
  configuredClean: string[],
  extras: string[],
  allNewTypes: string[],
  inUseClean: string[]
): string[] => {
  if (configuredClean.length > 0) {
    return [...configuredClean, ...extras, ...allNewTypes];
  }
  return [...uniqueSorted(inUseClean), ...allNewTypes];
};

/**
 * Apply ordering based on orderMode configuration
 * MANUAL mode preserves order, ALPHA mode sorts alphabetically
 */
export const applyOrdering = (
  types: string[],
  orderMode: PlaceTypeOrderMode | undefined,
  configuredClean: string[]
): string[] => {
  if (orderMode === PlaceTypeOrderMode.MANUAL && configuredClean.length > 0) {
    // Preserve manual order - types are already in correct order from mergeTypes
    return types;
  }
  // Sort alphabetically (ALPHA mode or undefined)
  // Create a copy to avoid mutating the input array (pure function)
  return [...types].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true })
  );
};
